import {
  Component,
  Input,
  OnInit,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import { SelectItemGroup } from "primeng/api";
import { Bind } from "primeng/bind";
import { Select } from "primeng/select";
import { SelectedChipsComponent } from "../../chip/selected-chips.component";
import { Checkbox } from "primeng/checkbox";
import { FormsModule } from "@angular/forms";

interface QuoteVariantOption {
  value: string;
  label: string;
  // Set where the in-group label is ambiguous on its own, e.g. "FCL" exists
  // under both Ocean and Rail. Falls back to label.
  chipLabel?: string;
}

const quoteVariantGroups: { label: string; items: QuoteVariantOption[] }[] = [
  {
    label: "Ocean",
    items: [
      { value: "fcl-sea", label: "FCL", chipLabel: "FCL Ocean" },
      { value: "lcl-sea", label: "LCL", chipLabel: "LCL Ocean" },
      { value: "fcltariff", label: "FCL Tariff" },
    ],
  },
  {
    label: "Rail",
    items: [
      { value: "fcl-rail", label: "FCL", chipLabel: "FCL Rail" },
      { value: "lcl-rail", label: "LCL", chipLabel: "LCL Rail" },
    ],
  },
  {
    label: "Air",
    items: [
      { value: "air", label: "Air Freight" },
      { value: "airtariff", label: "Air Tariff" },
    ],
  },
  {
    label: "Road",
    items: [
      { value: "ftl", label: "FTL" },
      { value: "ltl", label: "LTL" },
      { value: "ptl", label: "PTL" },
    ],
  },
  {
    label: "Additional",
    items: [
      { value: "brokerage", label: "Brokerage" },
      { value: "email", label: "Email" },
      { value: "manual", label: "Manual" },
      { value: "multi", label: "Multimodal" },
    ],
  },
];

@Component({
  selector: "app-quote-variant-multiselect",
  templateUrl: "./quote-variant-multiselect.component.html",
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [Bind, Select, Checkbox, FormsModule, SelectedChipsComponent],
})
export class QuoteVariantMultiselectComponent implements OnInit {
  readonly appendTo = input("");
  readonly disabled = input(false);
  readonly required = input(false);
  @Input() selectedVariants: string[] = [];
  readonly selectionChange = output<string[]>();

  public variantGroups: SelectItemGroup[] = [];

  private readonly chipLabels = new Map<string, string>();

  ngOnInit() {
    this.buildVariantGroups();
  }

  onVariantChange() {
    if (this.selectedVariants && this.selectedVariants.length) {
      this.selectionChange.emit(this.selectedVariants);
    } else {
      this.selectionChange.emit(null);
    }
  }

  public readonly chipLabel = (variant: string): string =>
    this.getVariantLabel(variant);

  public removeVariant(variant: string) {
    this.selectedVariants = this.selectedVariants.filter(
      (value) => value !== variant,
    );
    this.onVariantChange();
  }

  public isSelected(variant: string): boolean {
    return this.selectedVariants?.includes(variant) ?? false;
  }

  public getVariantLabel(variant: string): string {
    return this.chipLabels.get(variant) ?? "";
  }

  private buildVariantGroups() {
    this.variantGroups = quoteVariantGroups.map((group) => ({
      label: group.label,
      items: group.items.map((option) => {
        this.chipLabels.set(option.value, option.chipLabel ?? option.label);

        return { label: option.label, value: option.value };
      }),
    }));
  }
}
